import { useEffect, useState } from "react";
import "./App.css";

const PUBLIC_LINKS = {
  home: "https://codxer.com/",
  official: "https://codxer.com/official/",
  apiDocs: "https://codxer.com/legal/api-docs.html",
  pricing: "https://codxer.com/legal/pricing.html",
  privacy: "https://codxer.com/legal/privacy.html",
  terms: "https://codxer.com/legal/terms.html",
  statusPage: "https://codxer.com/legal/status.html",
  contact: "https://codxer.com/legal/contact.html",
  apiBase: "https://api.codxer.com",
  account: "https://codxer.com/#account",
  rapidApiListing: "https://rapidapi.com/lucylucy201-qwIgDEA9a/api/codxer-robin-ai",
  poeBot: "https://poe.com/CodXERRobin",
  githubOrg: "https://github.com/codxer-com",
  thisSpace: "https://huggingface.co/spaces/codxer/codxer-robin",
};

const LOGO_URL = "https://codxer.com/assets/brand/codxer-logo-512.png?v=201";
const STATUS_URL = "https://api.codxer.com/api/public/status";

function ExternalLink({ href, children, className }) {
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const CAPABILITIES = [
  {
    title: "OpenAI-compatible API",
    body: "Drop-in /v1/chat/completions and /v1/models for apps, agents, and integrations.",
  },
  {
    title: "CodXER Evolution rotation",
    body: "Deterministic pinned-core Robin rotation: user-selected fixed AI per core, one fixed AI answers each turn — see codxer.com/docs/evolution-rotation/ (not Fusion, not automatic best-model selection).",
  },
  {
    title: "Streaming & tiers",
    body: "Server-sent streaming, plan-aware quotas, and marketplace channels where published.",
  },
  {
    title: "Hosted service",
    body: "Inference runs on CodXER infrastructure. This Space is documentation — not downloadable weights.",
  },
];

function statusTone(status) {
  if (status === "operational") return "ok";
  if (status === "maintenance" || status === "degraded" || status === "partial_outage") {
    return "warn";
  }
  return "bad";
}

function StatusPanel() {
  const [state, setState] = useState({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    async function load() {
      try {
        const res = await fetch(STATUS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setState({ phase: "live", data });
        }
      } catch (err) {
        if (!cancelled) {
          const reason =
            err && err.name === "AbortError"
              ? "Request timed out."
              : "Live status unavailable from this page (likely CORS or network).";
          setState({ phase: "fallback", reason });
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }

    load();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  if (state.phase === "loading") {
    return (
      <section className="panel status-panel" aria-live="polite">
        <h2>Platform status</h2>
        <p className="muted">Checking CodXER public status…</p>
      </section>
    );
  }

  if (state.phase === "fallback") {
    return (
      <section className="panel status-panel" aria-live="polite">
        <h2>Platform status</h2>
        <p className="muted">{state.reason}</p>
        <p>
          View the canonical status page:{" "}
          <ExternalLink href={PUBLIC_LINKS.statusPage}>codxer.com/legal/status.html</ExternalLink>
        </p>
      </section>
    );
  }

  const { data } = state;
  const tone = statusTone(data.status);
  const version = data.api && data.api.version ? data.api.version : "—";

  return (
    <section className="panel status-panel" aria-live="polite">
      <h2>Platform status</h2>
      <div className="status-row">
        <span className={`status-pill status-pill--${tone}`}>{data.status}</span>
        <span className="mono">API v{version}</span>
      </div>
      {data.maintenance_mode && data.maintenance_message ? (
        <p className="status-note">{data.maintenance_message}</p>
      ) : null}
      {data.components ? (
        <ul className="component-grid">
          {Object.entries(data.components).map(([key, value]) => (
            <li key={key}>
              <span className="component-name">{key.replace(/_/g, " ")}</span>
              <span className={`status-pill status-pill--${statusTone(value.status)}`}>
                {value.status}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="muted small">
        Snapshot from <code>{STATUS_URL}</code>. Full history on{" "}
        <ExternalLink href={PUBLIC_LINKS.statusPage}>the status page</ExternalLink>.
      </p>
    </section>
  );
}

function App() {
  return (
    <div className="page">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="brand-lockup">
          <img
            className="brand-logo"
            src={LOGO_URL}
            alt="CodXER"
            width={56}
            height={56}
            decoding="async"
          />
          <div>
            <p className="eyebrow">CodXER Robin</p>
            <h1 className="site-title">CodXER Standard + Evolution Robin rotation</h1>
          </div>
        </div>
        <nav className="header-nav" aria-label="Primary">
          <ExternalLink href={PUBLIC_LINKS.home}>codxer.com</ExternalLink>
          <ExternalLink href={PUBLIC_LINKS.apiDocs}>API docs</ExternalLink>
          <ExternalLink className="btn btn--primary" href={PUBLIC_LINKS.account}>
            Get API access
          </ExternalLink>
        </nav>
      </header>

      <main id="main" className="main">
        <section className="hero panel">
          <p className="hero-kicker">OpenAI-compatible · Standard synthesis + Evolution pinned-core rotation</p>
          <h2 className="hero-title">
            CodXER Evolution rotates user-pinned AIs turn by turn with shared context — not a Hugging Face model repo.
          </h2>
          <p className="hero-lead">
            CodXER Standard is multi-model synthesis mode. CodXER Evolution is deterministic pinned-core Robin rotation
            (one fixed AI per turn). See{" "}
            <ExternalLink href="https://codxer.com/docs/evolution-rotation/">codxer.com/docs/evolution-rotation/</ExternalLink>.
            This Space documents the product — it does not run inference here.
          </p>
          <div className="hero-cta">
            <ExternalLink className="btn btn--primary" href={PUBLIC_LINKS.apiDocs}>
              Read API documentation
            </ExternalLink>
            <ExternalLink className="btn btn--ghost" href={PUBLIC_LINKS.pricing}>
              Pricing &amp; plans
            </ExternalLink>
          </div>
          <p className="disclaimer">
            <strong>Not model weights.</strong> There is no checkpoint to download. Robin exposes
            OpenAI-style endpoints at <code>api.codxer.com</code> backed by CodXER routing.
          </p>
        </section>

        <section className="panel">
          <h2>Capabilities</h2>
          <ul className="capability-grid">
            {CAPABILITIES.map((item) => (
              <li key={item.title} className="capability-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <StatusPanel />

        <section className="panel">
          <h2>Official links</h2>
          <div className="link-columns">
            <div>
              <h3>Product</h3>
              <ul className="link-list">
                <li>
                  <ExternalLink href={PUBLIC_LINKS.home}>codxer.com</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.apiDocs}>API documentation</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.pricing}>Pricing</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.privacy}>Privacy policy</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.terms}>Terms of service</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.contact}>Contact &amp; support</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.official}>Official identity</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.githubOrg}>Official GitHub organization</ExternalLink>
                </li>
              </ul>
            </div>
            <div>
              <h3>API &amp; channels</h3>
              <ul className="link-list">
                <li>
                  <ExternalLink href={PUBLIC_LINKS.apiBase}>api.codxer.com</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.rapidApiListing}>RapidAPI — CodXER Robin AI</ExternalLink>
                </li>
                <li>
                  <ExternalLink href={PUBLIC_LINKS.poeBot}>Poe — CodXERRobin bot</ExternalLink>
                </li>
              </ul>
              <p className="muted small">
                Public marketplace listings only. Seller, provider, and creator consoles are not linked here.
              </p>
            </div>
          </div>
        </section>

        <section className="panel cta-panel">
          <h2>Start building</h2>
          <p>
            Create a CodXER account, generate an API key, and call{" "}
            <code>POST /v1/chat/completions</code> with standard OpenAI client libraries.
          </p>
          <ExternalLink className="btn btn--primary" href={PUBLIC_LINKS.account}>
            Open CodXER account
          </ExternalLink>
        </section>
      </main>

      <footer className="site-footer">
        <p>
          © {new Date().getFullYear()} CodXER ·{" "}
          <ExternalLink href={PUBLIC_LINKS.privacy}>Privacy</ExternalLink> ·{" "}
          <ExternalLink href={PUBLIC_LINKS.thisSpace}>This Space</ExternalLink>
        </p>
      </footer>
    </div>
  );
}

export default App;
